/**
 * KpiRow — linha de cards KPI do portfólio FinOps (SP3).
 *
 * Exibe 4 cards lado a lado (SimpleGrid responsive):
 *  1. Custo do período (formatMoney)
 *  2. Variação MoM% e YoY% com seta direcional e cor semântica:
 *       alta de custo → red.6 (↑) | queda → green.6 (↓) | null → "—" sem cor
 *  3. Budget usado: percentual + semáforo (green.6/yellow.6/red.6)
 *  4. Economia potencial: formatMoney, 0 → "—"
 *
 * Usa `SmallBox` do DS para visual consistente com o restante do painel.
 * Quando `is_partial=true`, exibe `PartialDataBadge` no card de custo.
 * Cores apenas via tokens DS/Mantine. Sem fetch interno.
 */
import { Box, Group, SimpleGrid, Stack, Text } from '@mantine/core'
import {
  IconArrowDown,
  IconArrowUp,
  IconCurrencyDollar,
  IconPigMoney,
  IconReportMoney,
  IconWallet,
} from '@tabler/icons-react'

import { WidgetCard } from '@/components/ui'
import { formatMoney } from '@/components/finops/formatMoney'
import { formatPct } from '@/utils/finops'
import type { PortfolioKpis } from '@/api/finopsDashboard'
import { PartialDataBadge } from './PartialDataBadge'

// ─── helpers ────────────────────────────────────────────────────────────────

/** Cor semântica Mantine para variação: alta de custo=vermelho, queda=verde. */
function pctColor(value: number | null): string {
  if (value === null) return 'dimmed'
  return value > 0 ? 'red.6' : 'green.6'
}

/** Ícone direcional para variação percentual. */
function PctArrow({ value }: { value: number | null }) {
  if (value === null || value === 0) return null
  return value > 0 ? (
    <IconArrowUp size={14} />
  ) : (
    <IconArrowDown size={14} />
  )
}

/**
 * Percentual de orçamento coerente: usa apenas o custo dos tenants que possuem
 * orçamento (budgeted_cost_usd) sobre o total orçado — evita distorção causada
 * por tenants sem orçamento aumentando artificialmente o numerador.
 * Retorna "—" quando budget_total_usd === 0 (sem orçamento cadastrado).
 */
function budgetPctLabel(kpis: PortfolioKpis): string {
  if (!kpis.budget_total_usd || kpis.budget_total_usd <= 0) return '—'
  const pct = (kpis.budgeted_cost_usd / kpis.budget_total_usd) * 100
  return `${pct.toFixed(0)}%`
}

/** Cor semáforo de budget baseada em budgeted_cost_usd / budget_total_usd. */
function budgetSemaphoreColor(kpis: PortfolioKpis): string {
  if (!kpis.budget_total_usd || kpis.budget_total_usd <= 0) return 'gray.5'
  const pct = (kpis.budgeted_cost_usd / kpis.budget_total_usd) * 100
  if (pct >= 100) return 'red.6'
  if (pct >= 80) return 'yellow.6'
  return 'green.6'
}

// ─── sub-componente: card KPI simples ───────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

function KpiCard({ icon, title, children }: KpiCardProps) {
  return (
    <WidgetCard accent="none" p="md" style={{ minWidth: 0 }}>
      <Group gap="xs" mb={6} wrap="nowrap">
        <Box c="inovareTeal.6" style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {title}
        </Text>
      </Group>
      {children}
    </WidgetCard>
  )
}

// ─── componente principal ────────────────────────────────────────────────────

export interface KpiRowProps {
  kpis: PortfolioKpis
  is_partial?: boolean
  as_of?: string | null
}

export function KpiRow({ kpis, is_partial = false, as_of }: KpiRowProps) {
  const savingsPotential = kpis.savings_potential_usd
  const savingsLabel =
    savingsPotential > 0 ? formatMoney(savingsPotential) : '—'

  return (
    <SimpleGrid
      cols={{ base: 1, xs: 2, md: 4 }}
      spacing="sm"
    >
      {/* 1. Custo do período */}
      <KpiCard
        icon={<IconCurrencyDollar size={16} />}
        title="Custo do período"
      >
        <Stack gap={2}>
          <Group gap="xs" align="baseline" wrap="nowrap">
            <Text fw={700} size="xl" lh={1.1}>
              {formatMoney(kpis.total_usd)}
            </Text>
            {is_partial && (
              <PartialDataBadge is_partial={is_partial} as_of={as_of} />
            )}
          </Group>
          <Text size="xs" c="dimmed">custo total consolidado</Text>
        </Stack>
      </KpiCard>

      {/* 2. Variação MoM / YoY */}
      <KpiCard
        icon={<IconReportMoney size={16} />}
        title="Variação"
      >
        <Stack gap={4}>
          {/* MoM */}
          <Group gap={4} align="center" wrap="nowrap">
            <Text size="xs" c="dimmed" w={32} style={{ flexShrink: 0 }}>MoM</Text>
            <Group
              gap={2}
              align="center"
              c={pctColor(kpis.mom_pct)}
              style={{ fontWeight: 700, fontSize: 'var(--mantine-font-size-sm)' }}
              wrap="nowrap"
            >
              <PctArrow value={kpis.mom_pct} />
              <Text fw={700} size="sm" c={pctColor(kpis.mom_pct)} span>
                {formatPct(kpis.mom_pct, { signed: true })}
              </Text>
            </Group>
          </Group>

          {/* YoY */}
          <Group gap={4} align="center" wrap="nowrap">
            <Text size="xs" c="dimmed" w={32} style={{ flexShrink: 0 }}>YoY</Text>
            <Group
              gap={2}
              align="center"
              c={pctColor(kpis.yoy_pct)}
              style={{ fontWeight: 700, fontSize: 'var(--mantine-font-size-sm)' }}
              wrap="nowrap"
            >
              {kpis.yoy_pct !== null && <PctArrow value={kpis.yoy_pct} />}
              <Text fw={700} size="sm" c={pctColor(kpis.yoy_pct)} span>
                {kpis.yoy_pct !== null
                  ? formatPct(kpis.yoy_pct, { signed: true })
                  : '—'}
              </Text>
            </Group>
          </Group>
        </Stack>
      </KpiCard>

      {/* 3. Budget usado (semáforo) */}
      <KpiCard
        icon={<IconWallet size={16} />}
        title="Budget usado"
      >
        <Stack gap={2}>
          <Text
            fw={700}
            size="xl"
            lh={1.1}
            c={budgetSemaphoreColor(kpis)}
          >
            {budgetPctLabel(kpis)}
          </Text>
          {kpis.budget_total_usd > 0 ? (
            <>
              <Text size="xs" c="dimmed">
                de {formatMoney(kpis.budget_total_usd)} orçados
              </Text>
              {kpis.tenants_without_budget > 0 && (
                <Text size="xs" c="dimmed">
                  {kpis.tenants_without_budget}{' '}
                  {kpis.tenants_without_budget === 1
                    ? 'tenant sem orçamento'
                    : 'tenants sem orçamento'}
                </Text>
              )}
            </>
          ) : (
            <Text size="xs" c="dimmed">sem orçamento cadastrado</Text>
          )}
        </Stack>
      </KpiCard>

      {/* 4. Economia potencial */}
      <KpiCard
        icon={<IconPigMoney size={16} />}
        title="Economia potencial"
      >
        <Stack gap={2}>
          <Text fw={700} size="xl" lh={1.1}>
            {savingsLabel}
          </Text>
          <Text size="xs" c="dimmed">
            {savingsPotential > 0
              ? 'baseado em recomendações ativas'
              : 'nenhuma recomendação pendente'}
          </Text>
        </Stack>
      </KpiCard>
    </SimpleGrid>
  )
}
